import { useId, useState } from 'react';
import { milliToDays, schemeText } from '../pricing/units';
import { EquipmentDiscount } from './EquipmentDiscount';
import { bpToPct, type Quote, type Version } from './types';

/**
 * "Info" of the quote (owner 2026-10-20 23:37 and 2026-10-22): the line under the title — who made the version, who is
 * responsible for the quote, the day scheme, days, VAT, the packlist — stays out of sight behind a small "ⓘ Info ▸",
 * collapsed by default, the choice remembered per person on this computer. Facts here, changed where they were (⚙
 * "Ustawienia wersji", "…" → "Przekaż wycenę…") — except "Rabat na urządzenia" of a draft, set right here (owner
 * 2026-10-23: "rabat tez przenies do info"; before it was a bar over the table).
 */

const key = (userId: string) => `quotes.info.open:${userId}`;

function loadOpen(userId: string): boolean {
  try {
    return window.localStorage.getItem(key(userId)) === '1';
  } catch {
    return false;
  }
}

function saveOpen(userId: string, open: boolean): void {
  try {
    window.localStorage.setItem(key(userId), open ? '1' : '0');
  } catch {
    // storage blocked: it opens collapsed next time
  }
}

export function QuoteInfo({
  userId,
  quote,
  version,
  editable,
  packlistDiffers,
  discount,
}: {
  userId: string;
  quote: Quote;
  version: Version;
  editable: boolean;
  packlistDiffers?: boolean | undefined;
  /** Given for an editable draft to a role that sees the discount (rule 7a): the field "Rabat na urządzenia". */
  discount?: { onSet: () => void; onConflict: () => void } | undefined;
}) {
  const [open, setOpen] = useState(() => loadOpen(userId));
  const id = useId();
  const toggle = () => {
    setOpen(!open);
    saveOpen(userId, !open);
  };
  const rows: Array<[string, string]> = [];
  if (version.authorName) rows.push(['Przygotował(a)', version.authorName]);
  if (quote.responsibleUserId !== undefined && quote.responsibleName) rows.push(['Odpowiedzialny za wycenę', quote.responsibleName]);
  rows.push(['Schemat dni', schemeText(version.dayPctsBp)], ['Dni', milliToDays(version.defaultDaysMilli)]);
  // a draft sets the discount here (the field, below); a frozen version only says it
  if (!editable && (version.discountBp ?? 0) > 0) rows.push(['Rabat na urządzenia', `${bpToPct(version.discountBp!)}%`]);
  rows.push(['VAT', `${bpToPct(version.vatBp)}%`]);
  if (packlistDiffers !== undefined) rows.push(['Sprzętówka', packlistDiffers ? 'różni się od kosztorysu' : 'zgodna z kosztorysem']);
  const discountField = editable && discount;
  return (
    <div className={`q-info${open ? ' open' : ''}`} data-feature-id="quotes.info">
      <button className="q-info-toggle" type="button" aria-expanded={open} aria-controls={id} onClick={toggle}>
        <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
          <circle cx="8" cy="8" r="6.5" fill="none" stroke="currentColor" strokeWidth="1.3" />
          <path d="M8 7.2v4M8 4.9v.1" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
        Info
        <svg className="q-info-chevron" width="12" height="12" viewBox="0 0 16 16" aria-hidden="true">
          <path d="M6 3l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <dl id={id} className="q-meta" role="group" aria-label="Informacje o kosztorysie">
          {rows.map(([label, value]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
          {discountField && (
            <div className="q-meta-discount">
              <dt>Rabat na urządzenia</dt>
              <dd>
                <EquipmentDiscount version={version} onDiscountSet={discountField.onSet} onConflict={discountField.onConflict} />
              </dd>
            </div>
          )}
        </dl>
      )}
    </div>
  );
}
